import React, { useContext, useEffect, useState } from "react";
import { FiRefreshCw, FiUploadCloud, FiPlus, FiImage } from "react-icons/fi";
import { toast } from "react-hot-toast";

import useClients from "../../../Services/ClientServices/ClientConfig/useClientConfig";
import { GlobalContext } from "../../../../Context/GlobalContext";
import { uploadItemImage } from "../../../../Utils/imageUploadUtils";

const createEmptyGallery = () => ({
  image1: "",
  image2: "",
  image3: "",
  image4: "",
  image5: "",
  image6: "",
  image7: "",
  image8: "",
  image9: "",
  image10: "",
});

function GallerySection({ clientId }) {
  const { anlookApi } = useContext(GlobalContext);

  const { getGallery, updateGallery } = useClients();

  const [gallery, setGallery] = useState(createEmptyGallery());

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  // =========================================================
  // NORMALIZE IMAGE URL
  // =========================================================

  const normalizeImageUrl = (url = "") => {
    if (!url) return "";

    const cleanUrl = String(url).trim();

    if (!cleanUrl) return "";

    if (cleanUrl.startsWith("http://") || cleanUrl.startsWith("https://")) {
      return cleanUrl;
    }

    if (cleanUrl.startsWith("/")) {
      return `https://bumppy.in${cleanUrl}`;
    }

    return `https://bumppy.in/${cleanUrl}`;
  };

  // =========================================================
  // FETCH GALLERY
  // =========================================================

  const fetchGallery = async () => {
    if (!clientId) return;

    setLoading(true);

    try {
      const apiData = await getGallery(clientId);

      console.log("Gallery API Data:", apiData);

      const row = Array.isArray(apiData) ? apiData[0] || {} : {};

      const mappedGallery = createEmptyGallery();

      for (let i = 1; i <= 10; i++) {
        mappedGallery[`image${i}`] = normalizeImageUrl(
          row?.[`image${i}`] || "",
        );
      }

      console.log("Mapped Gallery:", mappedGallery);

      setGallery(mappedGallery);
    } catch (error) {
      console.error("Fetch gallery error:", error);

      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchGallery();
    }
  }, [clientId]);

  // =========================================================
  // UPLOAD IMAGE
  // =========================================================

  const uploadImage = async (imageKey, file, event) => {
    if (!file) return;

    if (!clientId) {
      toast.error("Client id missing");

      if (event?.target) {
        event.target.value = "";
      }

      return;
    }

    setUploadingKey(imageKey);

    const toastId = toast.loading("Uploading gallery image...");

    try {
      const result = await uploadItemImage({
        anlookApi,
        userid: clientId,
        itemName: imageKey,
        file,
        maxSizeMB: 5,
      });

      const imageUrl = result?.imageUrl;

      if (!imageUrl) {
        throw new Error("Image URL missing");
      }

      const uploadedImageUrl = normalizeImageUrl(imageUrl);

      setGallery((previousGallery) => ({
        ...previousGallery,
        [imageKey]: uploadedImageUrl,
      }));

      toast.success("Gallery image uploaded", {
        id: toastId,
      });
    } catch (error) {
      console.error("Gallery image upload error:", error);

      toast.error(error?.message || "Upload failed", {
        id: toastId,
      });
    } finally {
      setUploadingKey(null);

      if (event?.target) {
        event.target.value = "";
      }
    }
  };

  // =========================================================
  // SAVE GALLERY
  // =========================================================

  const saveGallery = async () => {
    if (!clientId) {
      toast.error("Client id missing");
      return;
    }

    setSaving(true);

    try {
      const success = await updateGallery({
        userid: clientId,

        image1: gallery.image1 || "",

        image2: gallery.image2 || "",

        image3: gallery.image3 || "",

        image4: gallery.image4 || "",

        image5: gallery.image5 || "",

        image6: gallery.image6 || "",

        image7: gallery.image7 || "",

        image8: gallery.image8 || "",

        image9: gallery.image9 || "",

        image10: gallery.image10 || "",
      });

      if (success) {
        await fetchGallery();
      }
    } catch (error) {
      console.error("Update gallery error:", error);

      toast.error("Failed to update gallery");
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // UI
  // =========================================================

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      {/* HEADER */}

      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-[11px] font-black uppercase tracking-[0.18em] text-gray-500">
            Website Gallery
          </h3>

          <p className="mt-1 text-xs font-semibold text-gray-500">
            Manage website gallery images
          </p>
        </div>

        <button
          type="button"
          onClick={saveGallery}
          disabled={saving}
          className="inline-flex h-9 items-center gap-2 rounded-xl bg-[#01C37D] px-4 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-[#01C37D]/20 hover:bg-[#00ad70] active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FiRefreshCw size={15} className={saving ? "animate-spin" : ""} />

          {saving ? "Updating..." : "Update Gallery"}
        </button>
      </div>

      {/* ===================================================
          LOADING
      =================================================== */}

      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <div className="h-7 w-7 animate-spin rounded-full border-4 border-[#01C37D]/20 border-t-[#01C37D]" />
        </div>
      ) : (
        /* =================================================
           GALLERY GRID
        ================================================= */

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 10 }, (_, i) => {
            const index = i + 1;

            const imageKey = `image${index}`;

            const imageUrl = gallery[imageKey];

            const isUploading = uploadingKey === imageKey;

            return (
              <div
                key={imageKey}
                className="rounded-2xl border border-gray-200 bg-[#F8FBFF] p-3 transition-all hover:border-[#01C37D]/40 hover:bg-white hover:shadow-md"
              >
                {/* TOP */}

                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                    Image {index}
                  </span>

                  <span
                    className={`rounded-full px-2 py-1 text-[10px] font-black uppercase ${
                      imageUrl
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {imageUrl ? "Added" : "Empty"}
                  </span>
                </div>

                {/* IMAGE UPLOAD */}

                <label
                  className={`group relative flex h-32 w-full items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white ${
                    isUploading ? "cursor-wait" : "cursor-pointer"
                  }`}
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={`Gallery ${index}`}
                      className="h-full w-full object-cover"
                      onError={(event) => {
                        console.log("Image load failed:", imageUrl);

                        event.currentTarget.style.opacity = "0.3";
                      }}
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-blue-400">
                      <FiUploadCloud size={26} />

                      <span className="text-[10px] font-black uppercase">
                        Upload
                      </span>
                    </div>
                  )}

                  {/* HOVER OVERLAY */}

                  <div className="absolute inset-0 hidden items-center justify-center bg-black/45 group-hover:flex">
                    {isUploading ? (
                      <FiRefreshCw
                        className="animate-spin text-white"
                        size={22}
                      />
                    ) : (
                      <FiPlus
                        className="text-white"
                        size={22}
                        strokeWidth={3}
                      />
                    )}
                  </div>

                  {/* FILE INPUT */}

                  <input
                    type="file"
                    accept="image/*"
                    disabled={isUploading}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];

                      uploadImage(imageKey, file, e);
                    }}
                  />
                </label>

                {/* FORMAT TEXT */}

                <div className="mt-2 flex items-center gap-1 text-[10px] font-semibold text-gray-400">
                  <FiImage size={12} />
                  JPG, PNG, WEBP, AVIF, GIF
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default GallerySection;
